// composables/useGoogleServices.js
// Reúne las llamadas a Google y sus métricas. No conoce pantallas, inputs concretos ni el store.
let loadingPromise = null;
let isLoaded = false;

// Convierte los componentes de una dirección de Google a los datos que usa la aplicación
export function procesarComponentesDireccion(place, position) {
  const addressComponents = place.addressComponents || [];

  const retorno_data = {
    streetNumber: null,
    streetName: null,
    commune: null,
    city: null,
    region: null,
    country: null,
    latitude: null,
    longitude: null,
  };

  addressComponents.forEach((component) => {
    const types = component.types;

    if (types.includes("street_number")) retorno_data.streetNumber = component.longText;
    if (types.includes("route")) retorno_data.streetName = component.longText;
    if (types.includes("locality")) retorno_data.city = component.longText;
    if (types.includes("administrative_area_level_3")) retorno_data.commune = component.longText;
    if (types.includes("administrative_area_level_1")) retorno_data.region = component.longText;
    if (types.includes("country")) retorno_data.country = component.longText;
  });

  if (position.latitude && position.longitude) {
    retorno_data.latitude = position.latitude;
    retorno_data.longitude = position.longitude;
  }

  retorno_data.address = [
    retorno_data.streetName,
    retorno_data.streetNumber,
    retorno_data.city || retorno_data.commune,
    retorno_data.region,
    retorno_data.country,
  ].filter(Boolean).join(", ");

  return retorno_data;
}

export default function useGoogleServices(origen) {
  const config = useRuntimeConfig();
  const { track } = useApiMetrics();

  function cargarGoogleMaps() {
    // Si ya está cargado, retornar inmediatamente
    if (isLoaded && window.google?.maps?.places) {
      return Promise.resolve(window.google.maps);
    }

    // Si ya hay una promesa en progreso, retornarla
    if (loadingPromise) {
      return loadingPromise;
    }

    const TIMEOUT = 15000;

    loadingPromise = new Promise((resolve, reject) => {
      // Verificar si ya existe el script
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');

      if (existingScript) {
        // Script ya existe, esperar a que se cargue
        const timeoutId = setTimeout(() => {
          clearInterval(checkInterval);
          // Resetear para que un próximo intento pueda reintentar la carga.
          // Sin esto, la promesa rechazada queda cacheada y los mapas quedan
          // muertos hasta recargar la página.
          loadingPromise = null;
          reject(new Error("Timeout esperando Google Maps existente"));
        }, TIMEOUT);

        const checkInterval = setInterval(() => {
          if (window.google?.maps?.places) {
            clearTimeout(timeoutId); // éxito: el timeout no debe resetear el singleton
            clearInterval(checkInterval);
            isLoaded = true;
            resolve(window.google.maps);
          }
        }, 100);
        return;
      }

      // Crear nuevo script
      const script = document.createElement("script");
      const apiKey = config.public.nuxtGoogleMapsKey;

      // Callback único global
      const callbackName = 'initGoogleMapsGlobal';
      window[callbackName] = () => {
        isLoaded = true;
        delete window[callbackName];
        // Solo se cuenta aquí: este es el único punto que descarga el SDK.
        // Las ramas "ya cargado" / "script existente" reutilizan y no son una carga nueva.
        track('google_maps', 'script_load', 'useGoogleServices');
        resolve(window.google.maps);
      };

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&v=weekly&loading=async&callback=${callbackName}`;
      script.async = true;
      script.defer = true;

      script.onerror = () => {
        loadingPromise = null;
        delete window[callbackName];
        reject(new Error("Error al cargar Google Maps"));
      };

      // Timeout de seguridad
      setTimeout(() => {
        if (!isLoaded) {
          loadingPromise = null;
          delete window[callbackName];
          reject(new Error("Timeout al cargar Google Maps"));
        }
      }, TIMEOUT);

      document.head.appendChild(script);
    });

    return loadingPromise;
  }

  async function cargarPlaces() {
    await cargarGoogleMaps();

    if (!window.google?.maps) {
      throw new Error('No fue posible inicializar Google Maps.');
    }

    await google.maps.importLibrary("places");

    if (!window.google?.maps?.places) {
      throw new Error('No fue posible inicializar Google Places.');
    }
  }

  async function cargarMarcadores() {
    if (!window.google?.maps?.marker) {
      await google.maps.importLibrary("marker");
    }
  }

  function crearMapa(contenedor, opciones) {
    const mapa = new google.maps.Map(contenedor, opciones);
    // Google factura Dynamic Maps por instancia de Map, no por descarga del SDK.
    track('google_maps', 'map_load', origen);
    return mapa;
  }

  function geocodificar(solicitud) {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode(solicitud, (results, status) => {
        if (status === "OK" && results?.length) {
          track('google_geocoding', 'geocode', origen);
          resolve(results);
        } else {
          reject(new Error(`Geocoder failed: ${status}`));
        }
      });
    });
  }

  // Devuelve la dirección de unas coordenadas con el mismo formato de Places
  async function geocodificarCoordenadas(latitud, longitud) {
    const results = await geocodificar({ location: { lat: latitud, lng: longitud } });
    const resultado = results[0];

    return {
      addressComponents: resultado.address_components.map((component) => ({
        longText: component.long_name,
        shortText: component.short_name,
        types: component.types,
      })),
      formattedAddress: resultado.formatted_address,
    };
  }

  function calcularRuta(solicitud) {
    return new Promise((resolve, reject) => {
      const directionsService = new google.maps.DirectionsService();

      directionsService.route(solicitud, (result, status) => {
        if (status == google.maps.DirectionsStatus.OK) {
          track('google_directions', 'route', origen);
          resolve(result);
        } else {
          reject(new Error(`Directions failed: ${status}`));
        }
      });
    });
  }

  // Para componentes con su propia interfaz de sugerencias
  function crearBuscadorLugares({ regiones = ['CL', 'AR'] } = {}) {
    let sessionToken = null;

    async function iniciarPlaces() {
      if (!window.google?.maps) {
        console.error('Google Maps no está disponible');
        return false;
      }

      try {
        await google.maps.importLibrary("places");

        if (!sessionToken) {
          sessionToken = new google.maps.places.AutocompleteSessionToken();
        }

        return true;
      } catch (error) {
        console.error('Error inicializando Places:', error);
        return false;
      }
    }

    async function buscarSugerencias(texto) {
      if (!texto || texto.length < 4) {
        return [];
      }

      const isReady = await iniciarPlaces();
      if (!isReady) {
        return [];
      }

      const request = {
        input: texto,
        sessionToken: sessionToken,
        includedRegionCodes: regiones,
      };

      try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        track('google_places', 'autocomplete', origen);
        return suggestions || [];
      } catch (error) {
        console.error('Error obteniendo predicciones:', error);
        return [];
      }
    }

    async function obtenerLugar(suggestion) {
      const place = await suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ['location', 'addressComponents']
      });
      track('google_places', 'place_details', origen);

      // Renovar el token de sesión
      sessionToken = new google.maps.places.AutocompleteSessionToken();

      return {
        place,
        coords: {
          latitude: place.location.lat(),
          longitude: place.location.lng()
        }
      };
    }

    return { buscarSugerencias, obtenerLugar };
  }

  // Agrega a un input la lista de sugerencias, el teclado y la selección
  function crearAutocomplete({ alSeleccionar, antiZoomSafari = false }) {
    const { buscarSugerencias, obtenerLugar } = crearBuscadorLugares();

    function crearContenedorSugerencias(input) {
      const container = document.createElement('div');
      container.className = 'pac-container';

      input.parentElement.style.position = 'relative';
      input.parentElement.appendChild(container);

      return container;
    }

    function mostrarSugerencias(suggestions, container, input) {
      if (!suggestions || suggestions.length === 0) {
        ocultarSugerencias(container);
        return;
      }

      container._suggestions = suggestions;
      container.innerHTML = '';
      container.style.display = 'block';

      suggestions.forEach((suggestion, index) => {
        const item = document.createElement('div');
        item.className = 'pac-item';
        item.dataset.index = index;

        const icon = document.createElement('span');
        icon.className = 'pac-item-icon';
        icon.innerHTML = '📍';

        const textContainer = document.createElement('div');
        textContainer.className = 'pac-item-text';

        const mainText = document.createElement('div');
        mainText.className = 'pac-item-main';
        mainText.textContent = suggestion.placePrediction?.text?.text || '';

        const secondaryText = document.createElement('div');
        secondaryText.className = 'pac-item-secondary';
        secondaryText.textContent = suggestion.placePrediction?.structuredFormat?.secondaryText?.text || '';

        textContainer.appendChild(mainText);
        textContainer.appendChild(secondaryText);
        item.appendChild(icon);
        item.appendChild(textContainer);

        item.addEventListener('click', async () => {
          await procesarSeleccionUbicacion(suggestion, input, container);
        });

        item.addEventListener('mouseenter', () => {
          const allItems = container.querySelectorAll('.pac-item');
          allItems.forEach(i => i.classList.remove('pac-item-selected'));
          item.classList.add('pac-item-selected');
        });

        container.appendChild(item);
      });
    }

    function ocultarSugerencias(container) {
      container.style.display = 'none';
      container.innerHTML = '';
      container._suggestions = null;
    }

    function navegarConTeclado(container, direccion) {
      const items = Array.from(container.querySelectorAll('.pac-item'));
      if (items.length === 0) return;

      let currentIndex = items.findIndex(item => item.classList.contains('pac-item-selected'));

      items.forEach(item => item.classList.remove('pac-item-selected'));

      if (currentIndex === -1) {
        currentIndex = direccion === 'down' ? 0 : items.length - 1;
      } else {
        if (direccion === 'down') {
          currentIndex = (currentIndex + 1) % items.length;
        } else {
          currentIndex = (currentIndex - 1 + items.length) % items.length;
        }
      }

      items[currentIndex].classList.add('pac-item-selected');
      items[currentIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    function seleccionarItemActual(container, input) {
      const selectedItem = container.querySelector('.pac-item-selected');
      if (!selectedItem) return false;

      const suggestions = container._suggestions;
      if (!suggestions) return false;

      const index = parseInt(selectedItem.dataset.index);
      const suggestion = suggestions[index];

      if (suggestion) {
        return procesarSeleccionUbicacion(suggestion, input, container);
      }

      return false;
    }

    async function procesarSeleccionUbicacion(suggestion, input, container) {
      try {
        const textoSeleccionado = suggestion.placePrediction?.text?.text || '';

        // Ocultar sugerencias y actualizar input
        if (input && container) {
          // Cancela la búsqueda pendiente e invalida cualquier respuesta en curso
          clearTimeout(container._debounceTimer);
          container._busquedaId = (container._busquedaId || 0) + 1;

          input.value = textoSeleccionado;
          ocultarSugerencias(container);
        }

        // Procesar el lugar seleccionado
        const { place, coords } = await obtenerLugar(suggestion);

        // Quien usa el autocompletado decide qué hacer con la dirección elegida
        await alSeleccionar(place, coords, textoSeleccionado);

        return true;
      } catch (error) {
        console.error('Error al procesar selección:', error);
        return false;
      }
    }

    function configurarAntiZoomSafari(input) {
      const viewport = document.querySelector('meta[name="viewport"]');
      const originalContent = viewport?.content;

      input.addEventListener('focus', () => {
        if (viewport) {
          viewport.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
        }
      });

      input.addEventListener('blur', () => {
        if (viewport && originalContent) {
          viewport.content = originalContent;
        }
      });
    }

    function configurarInput(input) {
      // Evita duplicar listeners y consultas sobre el mismo input
      if (input.hasAttribute('data-autocomplete-initialized')) {
        return false;
      }
      input.setAttribute('data-autocomplete-initialized', 'true');

      if (antiZoomSafari) {
        configurarAntiZoomSafari(input);
      }

      const oldContainer = input.parentElement?.querySelector('.pac-container');
      if (oldContainer) {
        oldContainer.remove();
      }

      const suggestionsContainer = crearContenedorSugerencias(input);

      input.addEventListener('input', async (e) => {
        const value = e.target.value;
        clearTimeout(suggestionsContainer._debounceTimer);

        const busquedaId = (suggestionsContainer._busquedaId || 0) + 1;
        suggestionsContainer._busquedaId = busquedaId;

        if (value.length < 4) {
          ocultarSugerencias(suggestionsContainer);
          return;
        }

        suggestionsContainer._debounceTimer = setTimeout(async () => {
          try {
            const predictions = await buscarSugerencias(value);

            // Ignora la respuesta si el usuario siguió escribiendo o ya eligió una dirección
            if (busquedaId !== suggestionsContainer._busquedaId) return;

            mostrarSugerencias(predictions, suggestionsContainer, input);
          } catch (error) {
            console.error('Error obteniendo predicciones:', error);
          }
        }, 300); // Espera 300 ms sin escribir antes de consultar Google, para reducir consultas y costos de Autocomplete
      });

      input.addEventListener('keydown', (e) => {
        const containerVisible = suggestionsContainer.style.display === 'block';

        if (e.key === 'ArrowDown' || e.keyCode === 40) {
          e.preventDefault();
          if (containerVisible) {
            navegarConTeclado(suggestionsContainer, 'down');
          }
        } else if (e.key === 'ArrowUp' || e.keyCode === 38) {
          e.preventDefault();
          if (containerVisible) {
            navegarConTeclado(suggestionsContainer, 'up');
          }
        } else if (e.key === 'Enter' || e.keyCode === 13) {
          if (containerVisible) {
            e.preventDefault();
            seleccionarItemActual(suggestionsContainer, input);
          }
        } else if (e.key === 'Escape' || e.keyCode === 27) {
          e.preventDefault();
          ocultarSugerencias(suggestionsContainer);
        }
      }, true);

      document.addEventListener('click', (e) => {
        if (!input.contains(e.target) && !suggestionsContainer.contains(e.target)) {
          ocultarSugerencias(suggestionsContainer);
        }
      });

      return true;
    }

    return { configurarInput };
  }

  return { cargarGoogleMaps, cargarPlaces, cargarMarcadores, crearMapa, geocodificar, geocodificarCoordenadas, calcularRuta, crearBuscadorLugares, crearAutocomplete };
}